import React, { useMemo } from 'react';
import { LineChart } from '@mui/x-charts/LineChart';
import dayjs from 'dayjs';
import { AiPresenceAudit } from '../../types/aiPresence';

// AI Presence Score over time for one product — overall plus one line per
// platform, fed the history table's rows (no extra request). Runs on a
// different benchmark prompt set aren't comparable, so the chart only plots
// the newest set's runs. Renders nothing below two points.

const SERIES = [
  { key: 'overall', label: 'Overall', color: 'var(--accent-text)' },
  { key: 'claude', label: 'Claude', color: '#C2410C' },
  { key: 'gemini', label: 'Gemini', color: '#2474DD' },
  { key: 'openai', label: 'OpenAI', color: '#1C7A5A' },
] as const;

type Point = { at: Date } & Record<(typeof SERIES)[number]['key'], number | null>;

export function AiPresenceScoreChart({ rows }: { rows: AiPresenceAudit[] }) {
  const { points, filtered } = useMemo(() => {
    const ready = rows
      .filter((r) => r.kind === 'product' && r.status === 'ready' && r.overall_score != null && (r.scored_at || r.created_at))
      .sort((a, b) => new Date(a.scored_at || a.created_at).getTime() - new Date(b.scored_at || b.created_at).getTime());
    const latestSet = ready.length ? ready[ready.length - 1].prompt_set_id : null;
    const sameSet = ready.filter((r) => r.prompt_set_id === latestSet);
    const ps = (r: AiPresenceAudit, k: string) => {
      const p = r.platform_scores && r.platform_scores[k];
      return p && p.score != null ? p.score : null;
    };
    return {
      filtered: ready.length - sameSet.length,
      points: sameSet.map(
        (r): Point => ({
          at: new Date(r.scored_at || r.created_at),
          overall: r.overall_score as number,
          claude: ps(r, 'claude'),
          gemini: ps(r, 'gemini'),
          openai: ps(r, 'openai'),
        }),
      ),
    };
  }, [rows]);

  if (points.length < 2) return null;
  const series = SERIES.filter((s) => points.some((p) => p[s.key] != null));

  return (
    <div className="border border-border-token rounded-[11px] bg-surface-2 px-4 py-3 mb-4">
      <div className="text-[12px] font-semibold text-text-2 mb-1">
        Score over time
        {filtered ? <span className="font-normal text-muted"> · current benchmark prompts only</span> : null}
      </div>
      <LineChart
        height={220}
        xAxis={[
          {
            scaleType: 'time',
            data: points.map((p) => p.at),
            valueFormatter: (d: Date) => dayjs(d).format('MMM DD'),
          },
        ]}
        yAxis={[{ min: 0, max: 100 }]}
        series={series.map((s) => ({
          label: s.label,
          color: s.color,
          curve: 'monotoneX' as const,
          showMark: true,
          connectNulls: true,
          data: points.map((p) => p[s.key]),
        }))}
        margin={{ top: 40, right: 16, bottom: 24, left: 36 }}
      />
    </div>
  );
}
